import {BlogPostBar} from '@/components/adminBar/blogPostBar';
import {
  NewsletterCta,
  type NewsletterCtaSubmitResult,
} from '@/components/newsletterCta/newsletterCta';
import {RichTextBlock} from '@/components/richTextBlock/richTextBlock';
import {Button} from '@/components/ui/button';
import {toastManager} from '@/components/ui/toast';
import {Typography} from '@/components/ui/typography';
import {buildBlogPostingSchema} from '@/lib/seo/blogPostingSchema';
import {adminBlogPostBySlugQuery, blogPostBySlugQuery} from '@/queries/blog';
import {submitWaitlist} from '@/server/waitlist';
import {formatDate} from '@/utils/displayFormatUtils';
import {getProfileName} from '@/utils/getProfileName';
import {Link, createFileRoute, useNavigate} from '@tanstack/react-router';
import {useEffect} from 'react';

const SITE_URL = import.meta.env.VITE_SITE_URL ?? 'https://wayra.cc';

export const Route = createFileRoute('/blog/$slug')({
  head: ({loaderData}) => {
    if (!loaderData?.post) return {};

    const {post} = loaderData;
    const ogImageUrl = `${SITE_URL}/api/og/blog/${post.id}`;
    const authorName = post.author
      ? getProfileName(post.author, 'Wayra')
      : 'Wayra';
    const description = post.excerpt;
    const schema = buildBlogPostingSchema(post, SITE_URL);
    const schemaJson = JSON.stringify(schema).replace(/<\//g, '<\\/');

    const canonicalUrl = `${SITE_URL}/blog/${post.slug}`;

    return {
      meta: [
        {title: `${post.title} — Wayra Blog`},
        {name: 'description', content: description},
        {property: 'og:title', content: post.title},
        {property: 'og:description', content: description},
        {property: 'og:image', content: ogImageUrl},
        {property: 'og:image:width', content: '1200'},
        {property: 'og:image:height', content: '630'},
        {property: 'og:image:alt', content: post.title},
        {property: 'og:url', content: canonicalUrl},
        {property: 'og:type', content: 'article'},
        {name: 'twitter:title', content: post.title},
        {name: 'twitter:description', content: description},
        {name: 'twitter:image', content: ogImageUrl},
        {name: 'twitter:image:alt', content: post.title},
        {name: 'twitter:card', content: 'summary_large_image'},
        {name: 'author', content: authorName},
      ],
      links: [
        {rel: 'canonical', href: canonicalUrl},
      ],
      scripts: [
        {
          type: 'application/ld+json',
          dangerouslySetInnerHTML: {__html: schemaJson},
        },
      ],
    };
  },
  component: BlogPostPage,
  loader: async ({params: {slug}, context}) => {
    const isSuperAdmin = context.profile?.level === 'superadmin';

    let post = null;
    if (isSuperAdmin) {
      post = await adminBlogPostBySlugQuery({data: slug});
    } else {
      post = await blogPostBySlugQuery({data: slug});
    }

    return {post, isSuperAdmin};
  },
});

/** Blog post detail page. Superadmins can preview draft posts; others are redirected if post not found. */
function BlogPostPage() {
  const {post, isSuperAdmin} = Route.useLoaderData();
  const navigate = useNavigate();

  useEffect(() => {
    if (!post) {
      toastManager.add({title: 'That page does not exist', type: 'error'});
      navigate({to: '/blog'});
    }
  }, [post, navigate]);

  if (!post) return null;

  const authorName = post.author
    ? getProfileName(post.author, 'Wayra')
    : 'Wayra';
  const authorInitial = authorName.charAt(0).toUpperCase();

  const authorAvatar = post.author?.avatarUrl ? (
    <img
      src={post.author.avatarUrl}
      alt={authorName}
      className="w-8 h-8 rounded-full object-cover"
      referrerPolicy="no-referrer"
    />
  ) : (
    <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-semibold">
      {authorInitial}
    </div>
  );

  const authorContent = (
    <>
      {authorAvatar}
      <Typography level="C1" className="text-gray-500">
        {authorName}
      </Typography>
    </>
  );

  return (
    <>
      <div className="max-w-3xl mx-auto w-full px-4 py-8">
        {isSuperAdmin && <BlogPostBar postId={post.id} status={post.status} />}
        <article className="overflow-hidden">
          {post.heroImageUrl && (
            <img
              src={post.heroImageUrl}
              alt=""
              className="w-full max-h-96 object-cover"
            />
          )}

          <Typography level="H1" className="mb-4">
            {post.title}
          </Typography>

          <div className="flex items-center gap-2 mb-8">
            {post.author ? (
              <Link
                to="/rider/$profileId"
                params={{profileId: post.author.id}}
                className="flex items-center gap-2 hover:opacity-80 transition-opacity"
              >
                {authorContent}
              </Link>
            ) : (
              authorContent
            )}
            <Typography level="C1" className="text-gray-400">
              ·
            </Typography>
            <Typography level="C1" className="text-gray-500">
              {formatDate(post.publishedAt ?? post.createdAt)}
            </Typography>
          </div>

          {post.tags.length > 0 && (
            <div className="flex flex-col gap-1">
              <Typography level="C3">Tags</Typography>
              <div className="flex flex-wrap gap-1.5 mb-8">
                {post.tags.map((tag) => (
                  <Link
                    key={tag.id}
                    to="/blog"
                    search={{page: 1, tag: tag.name}}
                  >
                    <Button type="button" variant="outline" size="xs">
                      {tag.name}
                    </Button>
                  </Link>
                ))}
              </div>
            </div>
          )}

          <hr className="mb-8 border-neutral-200" />

          <RichTextBlock html={post.content} />
        </article>
      </div>

      <NewsletterCta onSubmit={handleNewsletterSubmit} />
    </>
  );
}

async function handleNewsletterSubmit(
  email: string
): Promise<NewsletterCtaSubmitResult> {
  try {
    const response = await submitWaitlist({
      data: {email, source: 'blog-newsletter', userType: 'attendee'},
    });

    if (response.success) {
      return {success: true};
    }

    return {success: false, error: response.error};
  } catch (err) {
    const error =
      err instanceof Error
        ? err.message
        : 'Something went wrong. Please try again.';
    return {success: false, error};
  }
}
